import React, { createContext, useReducer, useContext, useCallback, useEffect } from 'react';
import CookiePopup from '../components/cookie_banner/cookie_popup';
import { getPersistentStore, getPersistentStoreMutex } from '@lemonadefashion/storage';

const POPUP_ENABLED = true;
const COOKIE = 'cookie_consent_v1';
const CONSENT_BANNER_BACKOFF_DAYS = 365;
const CONSENT_BY_DEFAULT = 1;

const CookieConsentStateContext = createContext({
  popupIsOpen: false,
  consent: 0,
  lastInteractTime: null
});
const CookieConsentApiContext = createContext({
  accept: () => { },
  reject: () => { }
});

const reducer = (state, action) => {
  switch (action.type) {
    case 'accept': return { ...state, popupIsOpen: false, consent: 1, lastInteractTime: action.ts };
    case 'reject': return { ...state, popupIsOpen: false, consent: 0, lastInteractTime: action.ts };
    default: throw new Error('Unhandled action type: ' + action.type);
  }
};

function _getStore() { return getPersistentStore(); }
function _getMutex() { return getPersistentStoreMutex(); }

function getCookieInfoFromStore() {
  const cookieInfo = _getStore().getItem(COOKIE);
  if (cookieInfo == null) return { consent: CONSENT_BY_DEFAULT, lastInteractTime: null };
  return JSON.parse(cookieInfo);
}

function setCookieInfoInStore(consent, lastInteractTime) {
  return new Promise((resolve, reject) => {
    const store = _getStore();
    const mutex = _getMutex();
    mutex.lock(COOKIE)
      .then(() => {
        store.setItem(COOKIE, JSON.stringify({ consent: consent, lastInteractTime: lastInteractTime }));
        return mutex.release(COOKIE).then(resolve).catch(reject);
      })
      .catch(reject);
  });
}

const CookieConsentProvider = ({ children }) => {
  const storageConsentState = getCookieInfoFromStore();
  const currentTime = Date.now() / 1000;

  const [state, dispatch] = useReducer(reducer, {
    popupIsOpen: storageConsentState.lastInteractTime == null ||
      Math.abs(storageConsentState.lastInteractTime - currentTime) > CONSENT_BANNER_BACKOFF_DAYS * 86400,
    consent: storageConsentState ? storageConsentState.consent : CONSENT_BY_DEFAULT,
    lastInteractTime: storageConsentState ? storageConsentState.lastInteractTime : null
  });
  const { popupIsOpen, consent, lastInteractTime } = state;

  useEffect(() => {
    setCookieInfoInStore(consent, lastInteractTime);
  }, [consent, lastInteractTime]);

  const accept = useCallback(() => {
    dispatch({ type: 'accept', ts: Date.now() / 1000 });
  }, []);

  const reject = useCallback(() => {
    dispatch({ type: 'reject', ts: Date.now() / 1000 });
  }, []);

  return (
    <CookieConsentStateContext.Provider value={state}>
      <CookieConsentApiContext.Provider value={{ accept, reject }}>
        {popupIsOpen && POPUP_ENABLED && <CookiePopup />}
        {children}
      </CookieConsentApiContext.Provider>
    </CookieConsentStateContext.Provider>
  );
};

function useCookieConsentState() {
  const context = useContext(CookieConsentStateContext);
  if (context === undefined) {
    throw new Error('useCookieConsentState must be used within CookieProvider');
  }
  return context;
}

function useCookieConsentApi() {
  const context = useContext(CookieConsentApiContext);
  if (context === undefined) {
    throw new Error('useCookieConsentApi must be used within CookieProvider');
  }
  return context;
}

export { CookieConsentProvider, useCookieConsentState, useCookieConsentApi };
